import { Lock, LockOpen } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import { PropsWithChildren, ReactNode } from "react";
import { FeatureFlags } from "../../Typings/FeatureFlags";
import { useGetUserFeatures } from "../../../Pages/ABAC/AbacAppQueries";
import { AppFeatureLookup, AppFeature } from "./AbacConstants";

interface AttributeProtectedComponentProps extends PropsWithChildren<unknown> {
  featureName: AppFeature;
  fallback?: ReactNode;
}

/**
 * Renders children if the user has access to the specified feature, otherwise renders an optional fallback.
 *
 * @example
 * ```tsx
 * <AttributeProtectedComponent
 *   featureName={AppFeatureLookup.FeaturesEnabledIcon}
 *   fallback={<LockOpen color="warning" fontSize="small" />}
 * >
 *   <Lock color="primary" fontSize="small" />
 * </AttributeProtectedComponent>
 * ```
 */
export default function AttributeProtectedComponent({
  children,
  featureName,
  fallback = null,
}: AttributeProtectedComponentProps) {
  const { enableAbacFeatures } = useFlags<FeatureFlags>();
  const { data: userAuthorizedFeatures } = useGetUserFeatures();
  const isUserAuthorized = userAuthorizedFeatures?.some((f) => f.includes(featureName));
  return enableAbacFeatures && isUserAuthorized ? <>{children}</> : <>{fallback}</>;
}

/**
 * Used as an example of how to use the AttributeProtectedComponent, this component renders a
 * lock icon if the user has access to the specified feature, otherwise renders an open lock icon.
 */
export function AbacFeaturesIndicator() {
  return (
    <AttributeProtectedComponent
      featureName={AppFeatureLookup.FeaturesEnabledIcon}
      fallback={<AbacIndicatorDisabled />}
    >
      <AbacIndicatorEnabled />
    </AttributeProtectedComponent>
  );
}

function AbacIndicatorEnabled() {
  return (
    <Tooltip title="ABAC Auth Enabled">
      <Lock color="primary" fontSize="small" />
    </Tooltip>
  );
}

function AbacIndicatorDisabled() {
  return (
    <Tooltip title="ABAC Auth Disabled">
      <LockOpen color="warning" fontSize="small" />
    </Tooltip>
  );
}
